import React, {
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  MultiMediaResponse,
  UserDTO,
} from 'dtos';
import Divider from '@mui/material/Divider';
import Controller from 'core/components/FormController';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import AutoCompletedInput from 'core/components/AutoComplete';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import useActiveStatusOptions from 'pages/Dashboard/hooks/useActiveStatusOptions';
import ProviderLookupListDropdown from 'views/EMR/components/ProviderLookup/Dropdown';
import DropdownOptionRow from 'core/components/Lookup/DropdownOptionRow';
import clsx from 'clsx';
import {
  FaxCreateData,
} from 'pages/Dashboard/pages/Fax/types';

export const followUpToDoMediaTypes = new Set(['OrderForm', 'ReferralOutgoing']);

export function isFollowUpSelection(mediaList: MultiMediaResponse[]): boolean {
  return mediaList.length > 0
    && mediaList.every((media) => followUpToDoMediaTypes.has(media.multimediaType ?? ''));
}

type Props = {
  isEligible: boolean;
  isSubmitting: boolean;
};

export default function FollowUpToDo({
  isEligible,
  isSubmitting,
}: Props) {
  const { control } = useFormContext<FaxCreateData>();
  const shouldCreateFollowUpTodo = useWatch({
    control,
    name: 'shouldCreateFollowUpTodo',
  });

  const { data: users = [], isLoading: usersLoading } = useGetUsers({
    onlyDoctors: false,
    enabled: isEligible,
  });
  const assigneeOptions = useMemo(
    () => users.filter((user) => !user?.isServiceAccount),
    [users],
  );
  const {
    visibleOptions: visibleAssigneeOptions,
    inactiveConfig: assigneeInactiveConfig,
  } = useActiveStatusOptions(assigneeOptions, 'user');

  return isEligible ? (
    <div className="flex flex-col gap-3 pt-3">
      <Divider />
      <div className="flex gap-4 items-center">
        <Controller
          control={control}
          name="shouldCreateFollowUpTodo"
          rules={{ deps: ['assignee'] }}
          render={({ field: { value, ...field } }) => (
            <Checkbox
              {...field}
              checked={Boolean(value)}
              id="create-follow-up-todo"
              className={value ? '!text-blue-500' : ''}
              disabled={isSubmitting}
              label={(
                <div className="font-semibold text-gray-500">
                  Create Follow-up To-do
                </div>
              )}
              onChange={(checked) => {
                field.onChange(checked);
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="assignee"
          rules={{
            validate: (value, formValues) => (
              !formValues.shouldCreateFollowUpTodo
              || !isNil(value)
              || 'To-do assignee is required'
            ),
          }}
          render={({ field, fieldState: { invalid } }) => (
            <Wrapper
              id="fax-follow-up-assignee"
              required={shouldCreateFollowUpTodo}
              labelClasses="font-semibold text-xs text-gray-500"
              className="!flex-1"
              error={invalid}
              showErrorText={false}
            >
              <AutoCompletedInput<UserDTO>
                {...field}
                id="fax-follow-up-assignee"
                blurOnSelect
                openOnFocus
                className="!w-full"
                options={visibleAssigneeOptions}
                disabled={!shouldCreateFollowUpTodo || usersLoading || isSubmitting}
                includeInputInList
                placeholder="Enter To-do assignee name"
                getLabel={(user) => getDisplayName(user.formattedNames)}
                isOptionEqualToValue={(a, b) => a.userId === (b as UserDTO).userId}
                classes={{ inputRoot: '!pr-0' }}
                dropdownClassName="!overflow-hidden p-2"
                slotProps={{
                  listbox: {
                    component: ProviderLookupListDropdown,
                    inactiveConfig: assigneeInactiveConfig,
                  },
                } as Object}
                renderOption={(optionProps, option) => (
                  <DropdownOptionRow
                    key={option.userId}
                    id={option.userId?.toString()}
                    label={getDisplayName(option.formattedNames, '--')}
                    props={optionProps}
                  >
                    <span className={clsx({ 'text-gray-400': option.isActive === false })}>
                      {getDisplayName(option.formattedNames, '--')}
                    </span>
                  </DropdownOptionRow>
                )}
                value={field.value}
                onChange={(value: UserDTO | UserDTO[]) => {
                  field.onChange(isNil(value) ? null : value as UserDTO);
                }}
              />
            </Wrapper>
          )}
        />
      </div>
    </div>
  ) : null;
}
